/**
 * Authentication Context
 * Manages user state, token storage, login/logout, and impersonation
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "vupp_admin_token";
const REAL_TOKEN_KEY = "vupp_admin_real_token"; // stashed during impersonation

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function isImpersonating() {
  return !!localStorage.getItem(REAL_TOKEN_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [token, setToken] = useState(() => getStoredToken());

  // Check session on mount
  useEffect(() => {
    const stored = getStoredToken();
    if (!stored) {
      setUser(false);
      return;
    }
    axios
      .get(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${stored}` },
      })
      .then((r) => {
        setUser(r.data);
        setToken(stored);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REAL_TOKEN_KEY);
        setUser(false);
        setToken(null);
      });
  }, []);

  const login = useCallback(async (username, password, turnstileToken = null) => {
    const { data } = await axios.post(`${API}/admin/auth/login`, {
      username,
      password,
      turnstile_token: turnstileToken,
    });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.removeItem(REAL_TOKEN_KEY); // fresh login clears impersonation state
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REAL_TOKEN_KEY);
    setToken(null);
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const stored = getStoredToken();
    if (!stored) return;
    const r = await axios.get(`${API}/admin/auth/me`, {
      headers: { Authorization: `Bearer ${stored}` },
    });
    setUser(r.data);
    return r.data;
  }, []);

  /**
   * Start impersonating a user. Stash current (admin) token, swap to new token.
   */
  const startImpersonation = useCallback((newToken, newUser) => {
    const realToken = getStoredToken();
    if (realToken) {
      localStorage.setItem(REAL_TOKEN_KEY, realToken);
    }
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  /**
   * Return to the real admin account (restore stashed token).
   */
  const stopImpersonation = useCallback(async () => {
    const realToken = localStorage.getItem(REAL_TOKEN_KEY);
    if (!realToken) return false;
    localStorage.setItem(TOKEN_KEY, realToken);
    localStorage.removeItem(REAL_TOKEN_KEY);
    setToken(realToken);
    // Refresh user data
    try {
      const r = await axios.get(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${realToken}` },
      });
      setUser(r.data);
      return true;
    } catch {
      // Token expired
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
      setToken(null);
      return false;
    }
  }, []);

  const value = {
    user,
    token,
    login,
    logout,
    refreshUser,
    startImpersonation,
    stopImpersonation,
    isImpersonating: isImpersonating(),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
